import api from '@/utils/api';
import { NotificationManager } from 'react-notifications';
import { presentCommissionError } from '@/utils/commission/errorPresenter';
import {
    FETCH_COMMISSION_APPROVALS,
    FETCH_COMMISSION_DASHBOARD,
    FETCH_COMMISSION_DASHBOARD_ITEM,
    FETCH_COMMISSION_DASHBOARD_LEDGER,
    FETCH_COMMISSION_DASHBOARD_ADJUSTMENTS,
    FETCH_COMMISSION_DASHBOARD_PAYMENTS,
    FETCH_COMMISSION_DASHBOARD_UPCOMING,
    FETCH_COMMISSION_DASHBOARD_HOLDS,
    FETCH_COMMISSION_PAYOUT_HISTORY,
    FETCH_COMMISSION_PAYOUTS,
    FETCH_COMMISSION_PERIODS,
    FETCH_COMMISSION_RECONCILIATION,
} from './types';

const handleError = (error, fallback, options) => presentCommissionError(error, fallback, options);
const notificationOnly = { presentation: 'notification' };

export const fetchCommissionDashboard = (filters = {}) => dispatch => (
    api.get('/commission-dashboard', { params: filters }).then(response => {
        const payload = response.data.data;
        dispatch({ type: FETCH_COMMISSION_DASHBOARD, payload });
        return payload;
    }).catch(error => handleError(error, 'Không tải được kết quả hoa hồng.', notificationOnly))
);

export const fetchCommissionDashboardItem = itemId => dispatch => (
    api.get(`/commission-dashboard/items/${itemId}`).then(response => {
        const payload = response.data.data;
        dispatch({ type: FETCH_COMMISSION_DASHBOARD_ITEM, payload });
        return payload;
    }).catch(error => handleError(error, 'Không tải được chi tiết khoản hoa hồng.', notificationOnly))
);

const fetchDashboardSection = (section, type, filters) => dispatch => (
    api.get(`/commission-dashboard/${section}`, { params: filters }).then(response => {
        const payload = response.data.data || { data: [] };
        dispatch({ type, payload });
        return payload;
    }).catch(error => handleError(error, 'Không tải được dữ liệu hoa hồng.', notificationOnly))
);

export const fetchCommissionDashboardLedger = filters => fetchDashboardSection('ledger', FETCH_COMMISSION_DASHBOARD_LEDGER, filters);
export const fetchCommissionDashboardAdjustments = filters => fetchDashboardSection('adjustments', FETCH_COMMISSION_DASHBOARD_ADJUSTMENTS, filters);
export const fetchCommissionDashboardPayments = filters => fetchDashboardSection('service-payments', FETCH_COMMISSION_DASHBOARD_PAYMENTS, filters);
export const fetchCommissionDashboardUpcoming = filters => fetchDashboardSection('upcoming', FETCH_COMMISSION_DASHBOARD_UPCOMING, filters);
export const fetchCommissionDashboardHolds = filters => fetchDashboardSection('holds', FETCH_COMMISSION_DASHBOARD_HOLDS, filters);

export const recalculateCommission = customerServiceId => () => (
    api.post(`/commission-calculations/customer-services/${customerServiceId}/recalculate`).then(response => {
        NotificationManager.success(response.data.msg || 'Đã tính lại hoa hồng.');
        return response.data.data;
    }).catch(error => handleError(error, 'Không thể tính lại hoa hồng.', { operation: 'RECALCULATE' }))
);

export const fetchCommissionPeriods = (filters = {}) => dispatch => (
    api.get('/commission-periods', { params: filters }).then(response => {
        const payload = response.data.data || [];
        dispatch({ type: FETCH_COMMISSION_PERIODS, payload });
        return payload;
    }).catch(error => handleError(error, 'Không tải được kỳ hoa hồng.', notificationOnly))
);

export const lockCommissionPeriod = (periodKey, filters = {}) => dispatch => (
    api.post(`/commission-periods/${periodKey}/lock`)
        .catch(error => handleError(error, 'Không thể khóa kỳ hoa hồng.', { operation: 'LOCK_PERIOD' }))
        .then(response => {
            const payload = response.data.data;
            NotificationManager.success('Đã khóa kỳ hoa hồng.');
            return dispatch(fetchCommissionPeriods(filters)).then(() => payload);
        })
);

export const finalizeCommissionAggregates = (periodKey, filters = {}) => dispatch => (
    api.post(`/commission-periods/${periodKey}/finalize-aggregates`)
        .catch(error => handleError(error, 'Không thể chốt tổng hợp hoa hồng.', { operation: 'FINALIZE_AGGREGATE' }))
        .then(response => {
            NotificationManager.success('Đã chốt tổng hợp hoa hồng của kỳ.');
            return dispatch(fetchCommissionPeriods(filters)).then(() => response.data.data);
        })
);

export const fetchCommissionReconciliation = filters => dispatch => (
    api.get('/commission-reconciliation', { params: filters }).then(response => {
        const payload = response.data.data;
        dispatch({ type: FETCH_COMMISSION_RECONCILIATION, payload });
        return payload;
    }).catch(error => handleError(error, 'Không tải được danh sách đối soát hoa hồng.', notificationOnly))
);

export const reconcileCommissionItem = (id, note) => () => (
    api.post(`/commission-reconciliation/items/${id}`, { note }).then(response => {
        NotificationManager.success('Đã xác nhận đối soát.');
        return response.data.data;
    }).catch(error => handleError(error, 'Không thể xác nhận đối soát.', { operation: 'RECONCILE' }))
);

export const fetchCommissionWorkflowFilters = isApproval => () => (
    api.get(isApproval ? '/commission-approvals/filters' : '/commission-reconciliation/filters')
        .then(response => response.data.data || {})
        .catch(error => handleError(error, 'Không tải được bộ lọc kiểm soát hoa hồng.', notificationOnly))
);

export const processCommissionWorkflowBatch = (isApproval, itemIds) => () => (
    api.post(isApproval ? '/commission-approvals/batch' : '/commission-reconciliation/batch', {
        item_ids: itemIds,
    }).then(response => response.data.data).catch(error => handleError(
        error,
        isApproval ? 'Không thể duyệt hoa hồng hàng loạt.' : 'Không thể xác nhận số liệu hàng loạt.',
        { operation: isApproval ? 'APPROVE' : 'RECONCILE' },
    ))
);

export const fetchCommissionApprovals = filters => dispatch => (
    api.get('/commission-approvals', { params: filters }).then(response => {
        const payload = response.data.data;
        dispatch({ type: FETCH_COMMISSION_APPROVALS, payload });
        return payload;
    }).catch(error => handleError(error, 'Không tải được danh sách duyệt hoa hồng.', notificationOnly))
);

export const decideCommissionItem = (id, decision, note) => () => (
    api.post(`/commission-approvals/items/${id}`, { decision, note }).then(response => {
        NotificationManager.success(decision === 'APPROVED' ? 'Đã duyệt khoản hoa hồng.' : 'Đã ghi nhận quyết định từ chối.');
        return response.data.data;
    }).catch(error => handleError(error, 'Không thể ghi nhận quyết định.', { operation: 'APPROVE' }))
);

export const fetchCommissionPayouts = (filters = {}) => dispatch => (
    api.get('/commission-payouts', { params: filters }).then(response => {
        const payload = response.data.data;
        dispatch({ type: FETCH_COMMISSION_PAYOUTS, payload });
        return payload;
    }).catch(error => handleError(error, 'Không tải được danh sách chi trả.', notificationOnly))
);

export const recordCommissionPayout = (id, payload) => () => (
    api.post(`/commission-payout-items/${id}/payments`, payload).then(response => {
        NotificationManager.success('Đã ghi nhận kết quả chi trả.');
        return response.data.data;
    }).catch(error => handleError(error, 'Không thể ghi nhận kết quả chi trả.', { operation: 'RECORD_PAYOUT' }))
);

export const fetchCommissionPayoutHistory = id => dispatch => (
    api.get(`/commission-payout-items/${id}/history`).then(response => {
        const payload = response.data.data || [];
        dispatch({ type: FETCH_COMMISSION_PAYOUT_HISTORY, payload });
        return payload;
    }).catch(error => handleError(error, 'Không tải được lịch sử chi trả.', notificationOnly))
);

export const fetchCommissionHolds = (filters = {}) => () => (
    api.get('/commission-holds', { params: filters }).then(response => response.data.data || {
        data: [],
        total: 0,
        current_page: 1,
        per_page: 30,
    })
        .catch(error => handleError(error, 'Không tải được danh sách khoản đang tạm giữ.', notificationOnly))
);

export const fetchCommissionIneligibleServices = (filters = {}) => () => (
    api.get('/commission-holds/ineligible-services', { params: filters }).then(response => response.data.data || {
        data: [],
        total: 0,
        current_page: 1,
        per_page: 15,
    }).catch(error => handleError(error, 'Không tải được danh sách dịch vụ chưa đủ điều kiện.', notificationOnly))
);

export const fetchCommissionProcessingJobs = (filters = {}) => () => (
    api.get('/commission-processing-jobs', { params: filters }).then(response => response.data.data || {
        data: [],
        total: 0,
        current_page: 1,
        per_page: 30,
    }).catch(error => handleError(error, 'Không tải được danh sách khoản cần xử lý hoa hồng.', notificationOnly))
);

export const retryCommissionProcessingJob = (jobId, options = {}) => () => (
    api.post(`/commission-processing-jobs/${jobId}/retry`).then(response => {
        if (options.notify !== false) {
            NotificationManager.success(response.data.msg || 'Đã thử lại việc tính hoa hồng.');
        }
        return response.data.data;
    }).catch(error => handleError(error, 'Không thể thử lại việc tính hoa hồng.', { operation: 'RECALCULATE' }))
);

export const recordCommissionCareOutcome = payload => () => (
    api.post('/commission-care-outcomes', payload).then(response => {
        NotificationManager.success(response.data.msg || 'Đã ghi nhận kết quả liên hệ khách hàng.');
        return response.data.data;
    }).catch(error => handleError(error, 'Không thể ghi nhận kết quả liên hệ khách hàng.', { operation: 'CARE_OUTCOME' }))
);

export const fetchCommissionPayoutSchedules = () => () => (
    api.get('/commission-payout-schedules').then(response => response.data.data || [])
        .catch(error => handleError(error, 'Không tải được lịch chi trả.', notificationOnly))
);

export const createCommissionPayoutSchedule = payload => () => (
    api.post('/commission-payout-schedules', payload).then(response => {
        NotificationManager.success('Đã tạo bản nháp lịch chi trả.');
        return response.data.data;
    }).catch(error => handleError(error, 'Không thể tạo lịch chi trả.', { operation: 'PAYOUT_SCHEDULE' }))
);

export const approveCommissionPayoutSchedule = id => () => (
    api.post(`/commission-payout-schedules/${id}/approve`).then(response => {
        NotificationManager.success('Đã duyệt lịch chi trả.');
        return response.data.data;
    }).catch(error => handleError(error, error?.response?.status === 403
        ? 'Bạn không có quyền duyệt lịch chi trả.'
        : 'Không thể duyệt lịch chi trả. Vui lòng tải lại để kiểm tra trạng thái mới nhất.', { operation: 'PAYOUT_SCHEDULE' }))
);

export const fetchCommissionAdjustments = (filters = {}) => () => (
    api.get('/commission-adjustments', { params: filters }).then(response => response.data.data || {
        data: [], total: 0, current_page: 1, per_page: 30,
    })
        .catch(error => handleError(error, 'Không tải được danh sách điều chỉnh hoa hồng.', notificationOnly))
);

export const requestCommissionAdjustment = (itemId, payload) => () => (
    api.post(`/commission-items/${itemId}/adjustments`, payload).then(response => {
        NotificationManager.success('Đã gửi yêu cầu điều chỉnh hoa hồng.');
        return response.data.data;
    }).catch(error => handleError(error, 'Không thể gửi yêu cầu điều chỉnh hoa hồng.', { operation: 'ADJUSTMENT' }))
);

export const decideCommissionAdjustment = (id, payload) => () => (
    api.post(`/commission-adjustments/${id}/decision`, payload).then(response => {
        NotificationManager.success('Đã ghi nhận quyết định điều chỉnh hoa hồng.');
        return response.data.data;
    }).catch(error => handleError(error, payload?.decision === 'REJECTED'
        ? 'Không thể từ chối điều chỉnh hoa hồng.'
        : 'Không thể duyệt điều chỉnh hoa hồng.', { operation: 'ADJUSTMENT' }))
);

export const fetchCommissionReceivables = (filters = {}) => () => (
    api.get('/commission-receivables', { params: filters }).then(response => response.data.data || {
        data: [], total: 0, current_page: 1, per_page: 30,
    })
        .catch(error => handleError(error, 'Không tải được công nợ hoa hồng nhân viên.', notificationOnly))
);

export const requestCommissionRecovery = (id, payload) => () => (
    api.post(`/commission-receivables/${id}/recovery-requests`, payload).then(response => {
        NotificationManager.success('Đã gửi yêu cầu khấu trừ công nợ.');
        return response.data.data;
    }).catch(error => handleError(error, 'Không thể gửi yêu cầu khấu trừ.', { operation: 'RECOVERY' }))
);

export const decideCommissionRecovery = (id, payload) => () => (
    api.post(`/commission-recovery-requests/${id}/decision`, payload).then(response => {
        NotificationManager.success('Đã ghi nhận quyết định khấu trừ.');
        return response.data.data;
    }).catch(error => handleError(error, 'Không thể duyệt khấu trừ công nợ.', { operation: 'RECOVERY' }))
);

export const fetchCommissionRefunds = filters => () => (
    api.get('/commission-refunds', { params: filters }).then(response => response.data.data || [])
        .catch(error => handleError(error, 'Không tải được danh sách đảo nguồn tính hoa hồng.', notificationOnly))
);

export const requestCommissionRefund = payload => () => (
    api.post('/commission-refunds', payload).then(response => {
        NotificationManager.success('Đã gửi yêu cầu đảo nguồn tính hoa hồng.');
        return response.data.data;
    }).catch(error => handleError(error, 'Không thể gửi yêu cầu đảo nguồn tính hoa hồng.', { operation: 'REFUND' }))
);

export const decideCommissionRefund = (id, payload) => () => (
    api.post(`/commission-refunds/${id}/decision`, payload).then(response => {
        NotificationManager.success('Đã ghi nhận quyết định đảo nguồn tính hoa hồng.');
        return response.data.data;
    }).catch(error => handleError(error, 'Không thể duyệt yêu cầu đảo nguồn tính hoa hồng.', { operation: 'REFUND' }))
);

export const cancelCommissionPayoutBatch = (id, reason) => () => (
    api.post(`/commission-payout-batches/${id}/cancel`, { reason }).then(response => {
        NotificationManager.success('Đã hủy danh sách chi trả.');
        return response.data.data;
    }).catch(error => handleError(error, 'Không thể hủy danh sách chi trả.', { operation: 'CANCEL_PAYOUT' }))
);

export const requestCommissionPayoutCorrection = (id, payload) => () => (
    api.post(`/commission-payout-transactions/${id}/correction-requests`, payload).then(response => {
        NotificationManager.success('Đã gửi yêu cầu điều chỉnh giao dịch chi trả.');
        return response.data.data;
    }).catch(error => handleError(error, 'Không thể gửi yêu cầu điều chỉnh giao dịch chi trả.', { operation: 'PAYOUT_CORRECTION' }))
);

export const decideCommissionPayoutCorrection = (id, payload) => () => (
    api.post(`/commission-payout-correction-requests/${id}/decision`, payload).then(response => {
        NotificationManager.success('Đã ghi nhận quyết định điều chỉnh giao dịch chi trả.');
        return response.data.data;
    }).catch(error => handleError(error, 'Không thể duyệt điều chỉnh giao dịch chi trả.', { operation: 'PAYOUT_CORRECTION' }))
);
